import React, { useEffect, useState, useCallback } from "react";
import { Button, Card, Row, Col, Badge, Form, Alert } from "react-bootstrap";
import axios from "axios";
import Layout from "../../components/layouts/Main";
import config from "../../config";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import { useHasPermission, PERMISSIONS } from "../../utils/permissions";
import PermissionGate from "../../components/common/PermissionGate";

// Tab Components
import PMPODetailsTab from "../../components/packagingMaterial/PO/PMPODetailsTab";
import PMPOGRNTab from "../../components/packagingMaterial/PO/PMPOGRNTab";
import PMQualityCheckTab from "../../components/packagingMaterial/PO/PMQualityCheckTab";
import PMUnloadingTab from "../../components/packagingMaterial/PO/PMUnloadingTab";
import PMOrderDocumentsPage from "../../components/packagingMaterial/PO/PMOrderDocumentsPage";
import PMPaymentTab from "../../components/packagingMaterial/PO/PMPaymentTab";

export default function PackagingMaterialPODetails() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [inwardEntries, setInwardEntries] = useState([]);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || "pm-po-details");

  // Permission checks
  const canViewPO = useHasPermission(PERMISSIONS.PM_PO_VIEW);
  const canEditPO = useHasPermission(PERMISSIONS.PM_PO_EDIT);
  const canDeletePO = useHasPermission(PERMISSIONS.PM_PO_DELETE);
  const canViewGRN = useHasPermission(PERMISSIONS.GRN_CREATE);
  const canViewQuality = useHasPermission(PERMISSIONS.QUALITY_CHECK_CREATE);
  const canViewUnloading = useHasPermission(PERMISSIONS.UNLOADING_ENTRY_ADD);
  const canViewDocuments = useHasPermission(PERMISSIONS.PO_DOC_ADD);
  const canViewPayments = useHasPermission(PERMISSIONS.PO_PAYMENT_ADD);

  const fetchOrder = useCallback(async () => {
    if (!canViewPO) {
      toast.error("You don't have permission to view packaging material PO details");
      return;
    }

    try {
      const res = await axios.get(
        `${config.API_BASE_URL}/pm-purchase-orders/${id}`
      );
      const { pmPO, inwardEntries } = res.data;
      setOrder(pmPO);
      setItems(pmPO.items || []);
      setInwardEntries(inwardEntries || []);
    } catch (err) {
      console.error("Error fetching packaging material PO:", err);
      toast.error("Error fetching packaging material PO details");
    }
  }, [id, canViewPO]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const getStatusBadge = (status) => {
    const map = {
      Draft: "secondary",
      Sent: "info",
      Confirmed: "primary",
      Partial: "warning",
      Received: "success",
      Cancelled: "danger",
      Closed: "dark",
    };
    return <Badge bg={map[status] || "dark"}>{status}</Badge>;
  };

  const getPaymentBadge = (status) => {
    const map = {
      Unpaid: "danger",
      Partial: "warning",
      Paid: "success",
    };
    return <Badge bg={map[status] || "secondary"}>{status}</Badge>;
  };

  const handleStatusChange = async (newStatus) => {
    if (!canEditPO) {
      toast.error("You don't have permission to update packaging material PO status");
      return;
    }

    try {
      await axios.put(`${config.API_BASE_URL}/pm-purchase-orders/${id}/status`, {
        status: newStatus
      });
      fetchOrder();
      toast.success("Status updated successfully");
    } catch (err) {
      console.error("Error updating status:", err);
      toast.error("Error updating status");
    }
  };

  // If user can't view PO, show permission denied
  if (!canViewPO) {
    return (
      <Layout>
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/packaging-material-purchase-orders">Packaging Material POs</Link>
            </li>
            <li className="breadcrumb-item active">Access Denied</li>
          </ol>
        </nav>
        <Alert variant="danger" className="d-flex align-items-center">
          <i className="fas fa-exclamation-triangle me-2"></i>
          <div>
            <strong>Access Denied</strong>
            <p className="mb-0">You don't have permission to view packaging material PO details. Contact your administrator for access.</p>
          </div>
        </Alert>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div className="text-center py-4">
          <PulseLoader color="#014b7cbb" size={9} />
        </div>
      </Layout>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case "pm-po-details":
        return <PMPODetailsTab order={order} items={items} />;
      case "grn":
        return canViewGRN ? <PMPOGRNTab pmPurchaseOrderId={order._id} order={order} inwardEntries={inwardEntries} onUpdate={fetchOrder} /> : null;
      case "quality-check":
        return canViewQuality ? <PMQualityCheckTab pmPurchaseOrderId={order._id} order={order} onUpdate={fetchOrder} /> : null;
      case "inward-entry":
        return canViewUnloading ? <PMUnloadingTab pmPurchaseOrderId={order._id} order={order} onUpdate={fetchOrder} /> : null;
      case "pm-order-docs":
        return canViewDocuments ? <PMOrderDocumentsPage pmPurchaseOrderId={order._id} /> : null;
      case "payments":
        return canViewPayments ? <PMPaymentTab pmPurchaseOrderId={order._id} order={order} /> : null;
      default:
        return <PMPODetailsTab order={order} items={items} />;
    }
  };

  return (
    <Layout>
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/packaging-material-purchase-orders">Packaging Material POs</Link>
          </li>
          <li className="breadcrumb-item active">#{order.poNumber}</li>
        </ol>
      </nav>

      <Row className="mb-3">
        {/* Top Summary Card */}
        <Card className="mb-4 shadow-sm border-0">
          <Card.Body>
            <Row className="align-items-center">
              {/* Left Section */}
              <Col md={4}>
                <h5 className="mb-2 text-primary">
                  <i className="fas fa-box me-2"></i>
                  Order #{order.poNumber}
                </h5>
                <p className="mb-1">
                  <i className="bi bi-person-badge-fill me-1"></i>
                  <strong>Supplier:</strong> {order.supplier?.business_name || "—"}
                </p>
                <p className="mb-1">
                  <i className="bi bi-phone-fill me-1"></i>
                  <strong>Contact:</strong> {order.supplier?.contact_person || "N/A"}
                </p>
                <p className="mb-0 text-muted">
                  <i className="fas fa-calendar me-1"></i>
                  <small>Created: {new Date(order.createdAt || order.orderDate).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  })}</small>
                </p>
              </Col>

              {/* Middle Section */}
              <Col md={4}>
                <div className="mb-2">
                  <strong>Status:</strong>
                  <PermissionGate
                    require={PERMISSIONS.PM_PO_EDIT}
                    fallback={
                      <span className="ms-2">
                        {getStatusBadge(order.status)}
                      </span>
                    }
                  >
                    <Form.Select
                      size="sm"
                      style={{
                        width: "auto",
                        display: "inline-block",
                        marginLeft: "10px",
                      }}
                      value={order.status}
                      onChange={(e) => handleStatusChange(e.target.value)}
                    >
                      <option value="Draft">Draft</option>
                      <option value="Sent">Sent</option>
                      <option value="Confirmed">Confirmed</option>
                      <option value="GRN Entry">GRN Entry</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Partial">Partial Received</option>
                      <option value="Received">Fully Received</option>
                      <option value="Cancelled">Cancelled</option>
                      <option value="Closed">Closed</option>
                    </Form.Select>
                  </PermissionGate>
                </div>
                <p className="mb-0">
                  <strong>Payment:</strong>{" "}
                  {getPaymentBadge(order.paymentStatus || 'Unpaid')}
                </p>
                {order.totalAmount && (
                  <p className="mb-0 mt-1">
                    <strong>Total:</strong>{" "}
                    <span className="text-success">₹{Number(order.totalAmount || 0).toFixed(2)}</span>
                  </p>
                )}
              </Col>

              {/* Right Section */}
              <Col md={4} className="text-md-end mt-3 mt-md-0">
                <div className="d-flex flex-wrap gap-2 justify-content-md-end">
                  <PermissionGate
                    require={PERMISSIONS.PM_PO_EDIT}
                    fallback={
                      <Button variant="outline-secondary" disabled size="sm" title="Edit access restricted">
                        <i className="bi bi-pencil-square me-1"></i> Edit
                      </Button>
                    }
                  >
                    <Link
                      className="btn btn-outline-warning btn-sm"
                      to={`/edit-packaging-material-po/${order._id}`}
                    >
                      <i className="bi bi-pencil-square me-1"></i> Edit
                    </Link>
                  </PermissionGate>

                  <Button variant="outline-primary" size="sm" onClick={() => window.print()}>
                    <i className="bi bi-file-earmark-pdf-fill me-1"></i> Print
                  </Button>

                  <PermissionGate require={PERMISSIONS.PM_PO_DELETE}>
                    <Button variant="outline-danger" size="sm" title="Delete Packaging Material PO">
                      <i className="fas fa-trash me-1"></i> Delete
                    </Button>
                  </PermissionGate>
                </div>

                {(!canEditPO && !canDeletePO) && (
                  <small className="text-muted d-block mt-1">
                    <i className="fas fa-eye me-1"></i>Read-only access
                  </small>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tab Navigation and Content */}
        <Card>
          <Card.Header>
            {/* Custom Tab Navigation */}
            <ul className="nav nav-tabs card-header-tabs">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "pm-po-details" ? "active" : ""}`}
                  onClick={() => setActiveTab("pm-po-details")}
                >
                  <i className="fas fa-info-circle me-1"></i>
                  PO Details
                </button>
              </li>

              {canViewGRN && (
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "grn" ? "active" : ""}`}
                    onClick={() => setActiveTab("grn")}
                  >
                    <i className="fas fa-file-invoice me-1"></i>
                    GRN
                  </button>
                </li>
              )}

              {canViewQuality && (
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "quality-check" ? "active" : ""}`}
                    onClick={() => setActiveTab("quality-check")}
                  >
                    <i className="fas fa-check-circle me-1"></i>
                    Quality Check
                  </button>
                </li>
              )}

              {canViewUnloading && (
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "inward-entry" ? "active" : ""}`}
                    onClick={() => setActiveTab("inward-entry")}
                  >
                    <i className="fas fa-dolly me-1"></i>
                    Inward Entry
                  </button>
                </li>
              )}

              {/* {canViewDocuments && (
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "pm-order-docs" ? "active" : ""}`}
                    onClick={() => setActiveTab("pm-order-docs")}
                  >
                    <i className="fas fa-file-alt me-1"></i>
                    Documents
                  </button>
                </li>
              )} */}

              {canViewPayments && (
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "payments" ? "active" : ""}`}
                    onClick={() => setActiveTab("payments")}
                  >
                    <i className="fas fa-credit-card me-1"></i>
                    Payments
                  </button>
                </li>
              )}
            </ul>
          </Card.Header>

          <Card.Body>
            {renderTabContent()}

            {/* Show message if user has limited access to tabs */}
            {!canViewGRN && !canViewQuality && !canViewUnloading && !canViewDocuments && !canViewPayments && activeTab !== "pm-po-details" && (
              <Alert variant="info" className="mt-3">
                <i className="fas fa-info-circle me-2"></i>
                <strong>Limited Access</strong>
                <p className="mb-0">
                  You have read-only access to this packaging material purchase order. Additional tabs require specific permissions:
                </p>
                <ul className="mt-2 mb-0">
                  <li>GRN - requires GRN creation permission</li>
                  <li>Quality Check - requires quality check creation permission</li>
                  <li>Inward Entry - requires unloading entry permission</li>
                  <li>Documents - requires document management permission</li>
                  <li>Payments - requires payment management permission</li>
                </ul>
              </Alert>
            )}
          </Card.Body>
        </Card>
      </Row>
    </Layout>
  );
}
